// Funkcja do inteligentnego formatowania cen - usuwanie zbednych zer
export const formatPrice = (price, maxDecimals = 2) => {
  if (price === null || price === undefined) return '0.00';
  const num = parseFloat(price);
  if (isNaN(num)) return '0.00';

  // Formatuj z maksymalna liczba miejsc dziesietnych
  const formatted = num.toFixed(maxDecimals);

  // Usun zbedne zera z konca (ale zostaw co najmniej 2 miejsca dla standardowych cen)
  if (maxDecimals > 2) {
    return formatted.replace(/\.?0+$/, '') || '0';
  }

  return formatted;
};

// Funkcja do mapowania jednostek na skrocone nazwy
export const getUnitDisplayName = (jednostka_wagi) => {
  const unitMap = {
    'gramy': 'gr',
    'tabletki': 'tab',
    'kapsulki': 'kaps',
    'sztuki': 'szt',
    'ml': 'ml',
    'nieustawiono': '—'
  };
  return unitMap[jednostka_wagi] || jednostka_wagi;
};

// Funkcja do pobierania formy jednostkowej dla wyswietlania (np. "tabletk" -> "tab")
export const getUnitSingularForm = (jednostka_wagi) => {
  const singularMap = {
    'tabletki': 'tab',
    'kapsulki': 'kaps',
    'sztuki': 'szt',
    'nieustawiono': '—'
  };
  return singularMap[jednostka_wagi] || jednostka_wagi.slice(0, -1);
};

// Pomocnicze funkcje do ekstrakcji danych dla etykiet
// Lista znanych slow smakowych/wariantowych do usuwania z konca nazwy
const FLAVOR_WORDS = new Set([
  // Angielskie
  'orange', 'chocolate', 'cherry', 'lemon', 'strawberry', 'vanilla', 'mango',
  'coconut', 'raspberry', 'banana', 'apple', 'pineapple', 'watermelon', 'grape',
  'peach', 'blueberry', 'cranberry', 'lime', 'mint', 'caramel', 'cookies',
  'cream', 'wild', 'berry', 'tropical', 'fruit', 'punch', 'cola', 'fresh',
  'flavour', 'flavourr', 'flavor', 'choco', 'cookie', 'peanut', 'butter',
  'hazelnut', 'almond', 'pistachio', 'coffee', 'mocha', 'toffee', 'honey',
  'grapefruit', 'passion', 'kiwi', 'melon', 'pear', 'plum', 'blackberry',
  'currant', 'pomegranate', 'lychee', 'guava', 'papaya', 'cinnamon',
  'bubblegum', 'marshmallow', 'brownie', 'nougat', 'white', 'dark', 'milk',
  'double', 'ice', 'tea', 'citrus', 'exotic', 'forest', 'mixed',
  // Polskie - smaki
  'cytryna', 'cytrynowy', 'cytrynowa', 'pomarancza', 'pomaranczowy', 'pomaranczowa',
  'truskawka', 'truskawkowy', 'truskawkowa', 'czekolada', 'czekoladowy', 'czekoladowa',
  'wisnia', 'wisniowy', 'wisniowa', 'malina', 'malinowy', 'malinowa',
  'banan', 'bananowy', 'bananowa', 'jablko', 'jablkowy', 'jablkowa',
  'ananas', 'ananasowy', 'ananasowa', 'wanilia', 'waniliowy', 'waniliowa',
  'mieta', 'mietowy', 'mietowa', 'karmel', 'karmelowy', 'karmelowa',
  'kokos', 'kokosowy', 'kokosowa', 'lesny', 'lesna', 'owocowy', 'owocowa',
  'tropikalny', 'tropikalna', 'grejpfrut', 'grejpfrutowy', 'arbuz', 'arbuzowy',
  'brzoskwinia', 'brzoskwiniowy', 'gruszka', 'gruszkowy', 'jagoda', 'jagodowy',
  'jezynowy', 'jezyna', 'porzeczka', 'porzeczkowy', 'smoczy', 'smoczyni',
  'rozany', 'rozana', 'melonowy', 'melonowa', 'poziomka', 'poziomkowy',
  // Slowa poprzedzajace smaki (do ignorowania)
  'smak', 'smaku', 'o', 'ze', 'z',
]);

// Funkcja do czyszczenia slowa z interpunkcji
const cleanWord = (word) => word.replace(/[.,;:!?()[\]{}'"]/g, '').toLowerCase();

// Popularne marki suplementow/lekow (fallback gdy lista producentow z bazy jest pusta)
const KNOWN_MANUFACTURERS = [
  'ACTIVLAB', 'ALLNUTRITION', 'OLIMP', 'TREC', 'BIOTECH', 'BIOTECH USA',
  'SCITEC', 'SCITEC NUTRITION', 'MYPROTEIN', 'OSTROVIT', 'NOW FOODS', 'NOW',
  'SOLGAR', 'SWANSON', "DOCTOR'S BEST", 'JARROW', 'JARROW FORMULAS',
  'LIFE EXTENSION', 'PURE ENCAPSULATIONS', 'THORNE', 'GARDEN OF LIFE',
  'NORDIC NATURALS', 'CALIFORNIA GOLD NUTRITION', 'NATURE MADE',
  'NATROL', 'SOURCE NATURALS', 'COUNTRY LIFE', 'BLUEBONNET',
  'APOLLO', 'APTEKAPRIMA', 'APTEKA PRIMA', 'USP ZDROWIE',
  'POLPHARMA', 'HASCO', 'AFLOFARM', 'ZENTIVA', 'KRKA', 'TEVA',
  'SFD', 'SFD NUTRITION', 'KFD', 'KFD NUTRITION', '4+ NUTRITION',
  'REAL PHARM', 'HI TEC', 'HI-TEC', 'HITEC', 'NUTREND',
  'EXTRIFIT', 'KEVIN LEVRONE', 'AMIX', 'MUTANT', 'BSN', 'OPTIMUM',
  'OPTIMUM NUTRITION', 'MUSCLETECH', 'CELLUCOR', 'GAT', 'NUTREX',
  'UNIVERSAL', 'UNIVERSAL NUTRITION', 'DYMATIZE', 'EVL', 'EVLUTION',
  'PROSUPPS', 'REDCON1', 'JYM', 'GHOST', 'RYSE', 'RULE ONE', 'R1',
  'IRON HORSE', 'FA', 'FITNESS AUTHORITY', 'FORMOTIVA', 'BIOMEDICA',
];

export const simplifyProductName = (fullName, manufacturers = []) => {
  if (!fullName) return '';

  let name = fullName.trim();

  // Krok 1: Usun producenta z poczatku nazwy
  // Polacz liste z bazy z fallback lista znanych producentow
  const mfrList = (manufacturers && manufacturers.length > 0)
    ? [...manufacturers].map(m => (m.nazwa || m).toString().trim()).filter(n => n.length > 0)
    : [];
  const allMfrs = [...new Set([...mfrList, ...KNOWN_MANUFACTURERS])];

  // Sortuj od najdluzszych (zeby ALLNUTRITION pasowalo przed ALL, BIOTECH USA przed BIOTECH)
  const sortedMfrs = allMfrs.sort((a, b) => b.length - a.length);

  for (const mfr of sortedMfrs) {
    const regex = new RegExp(`^${mfr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+`, 'i');
    if (regex.test(name)) {
      name = name.replace(regex, '');
      break;
    }
  }

  // Krok 2: Usun smak/wariant z konca (po gramaturze/ilosci)
  // Znajdz pozycje ostatniego wzorca ilosci (np. "500 g", "100 tab", "30 kap")
  const unitPattern = /(\d+(?:[.,]\d+)?)\s*(g|mg|ml|kg|l|tab|tabl|kap|kaps|szt|sasz|daw|porcji|caps|capsules)\b/gi;
  let lastUnitEnd = -1;
  let match;
  while ((match = unitPattern.exec(name)) !== null) {
    lastUnitEnd = match.index + match[0].length;
  }

  if (lastUnitEnd > 0 && lastUnitEnd < name.length) {
    // Jest tekst po gramaturze — sprawdz czy to smak/wariant
    const afterUnit = name.substring(lastUnitEnd).trim();
    const words = afterUnit.split(/\s+/);
    // Usuwaj slowa smakowe z konca (agresywnie - usun nawet jesli sa inne slowa przed)
    while (words.length > 0 && FLAVOR_WORDS.has(cleanWord(words[words.length - 1]))) {
      words.pop();
    }
    // Jesli wszystkie slowa usuniete lub zostaly tylko slowa ignorowane, usun calosc
    const remainingWords = words.filter(w => !FLAVOR_WORDS.has(cleanWord(w)));
    if (remainingWords.length === 0) {
      name = name.substring(0, lastUnitEnd).trim();
    } else {
      // Zostaw tylko do ostatniego slowa nie-smakowego
      name = name.substring(0, lastUnitEnd).trim() + ' ' + remainingWords.join(' ');
    }
  }

  // Krok 3: Fallback — jesli nie znaleziono gramatura, sprobuj usunac smak z konca
  if (lastUnitEnd <= 0) {
    const words = name.split(/\s+/);
    // Usuwaj slowa smakowe z konca (z czyszczeniem interpunkcji)
    while (words.length > 1 && FLAVOR_WORDS.has(cleanWord(words[words.length - 1]))) {
      words.pop();
    }
    name = words.join(' ');
  }

  // Krok 4: Wyczysc nadmiarowe spacje
  return name.replace(/\s+/g, ' ').trim();
};

export const extractPackageQuantity = (product, name, description, measureUnits = []) => {
  // Najpierw sprawdz czy produkt ma recznie ustawione wartosci
  if (product && product.ilosc_jednostek) {
    // Znajdz skrot jednostki dla jednostka_wagi
    let unitLabel = product.jednostka_wagi || product.jednostka || 'szt';

    // Jesli mamy measureUnits, sprobuj znalezc wlasciwy skrot
    if (measureUnits && measureUnits.length > 0) {
      const foundUnit = measureUnits.find(u =>
        u.nazwa === unitLabel ||
        u.skrot === unitLabel
      );
      if (foundUnit) {
        unitLabel = foundUnit.skrot;
      }
    }

    return `${product.ilosc_jednostek} ${unitLabel}`;
  }

  if (!name && !description) return '';

  const text = `${name || ''} ${description || ''}`;

  // Uzywaj wzorcow z bazy jednostek miary (measureUnits)
  if (measureUnits && measureUnits.length > 0) {
    for (const unit of measureUnits) {
      if (unit.wzorce_regex) {
        const patterns = unit.wzorce_regex.split('|').map(p => p.trim()).filter(p => p);
        if (patterns.length > 0) {
          const regexPattern = new RegExp(`(\\d+(?:[\\.,]\\d+)?)\\s*(?:${patterns.join('|')})\\b`, 'i');
          const match = text.match(regexPattern);
          if (match) {
            return `${match[1]} ${unit.skrot}`;  // np. "400 tab"
          }
        }
      }
    }
  }

  // Fallback na hardkodowane wzorce jesli brak jednostek z DB
  const fallbackPatterns = [
    { pattern: /(\d+)\s*(tab|tabs|tabl|tabletek|tabletki|tablets)\b/i, unit: 'tab' },
    { pattern: /(\d+)\s*(kaps|kap|caps|cap|kapsułek|kapsułki|capsules)\b/i, unit: 'kaps' },
    { pattern: /(\d+)\s*(sasz|saszetki|saszetka|sachets)\b/i, unit: 'sasz' },
    { pattern: /(\d+)\s*ml\b/i, unit: 'ml' },
    { pattern: /(\d+)\s*g\b(?!\s*mg)/i, unit: 'g' },
    { pattern: /(\d+)\s*kg\b/i, unit: 'kg' },
    { pattern: /(\d+)\s*(szt|sztuk|sztuki|pieces|pcs)\b/i, unit: 'szt' },
    { pattern: /(\d+)\s*(amp|ampułek|ampułki|ampoules)\b/i, unit: 'amp' },
    { pattern: /(\d+)\s*(fiol|fiolki|fiolka|vials)\b/i, unit: 'fiol' },
    { pattern: /(\d+)\s*(daw|dawki|dawka|doses|dawek)\b/i, unit: 'daw' },
    { pattern: /(\d+)\s*(porcji|porcje|servings)\b/i, unit: 'porcji' },
    { pattern: /x\s*(\d+)\b/i, unit: 'x' },
  ];

  for (const { pattern, unit } of fallbackPatterns) {
    const match = text.match(pattern);
    if (match) {
      return `${match[1]} ${unit}`;
    }
  }

  return '';
};

// Funkcja do automatycznego wykrywania jednostki i ilosci z nazwy produktu
export const extractUnitAndQuantity = (name, description, measureUnits = []) => {
  if (!name && !description) return { quantity: '', unit: 'nieustawiono' };

  const text = `${name || ''} ${description || ''}`.toLowerCase();

  // Uzyj jednostek z bazy danych jesli dostepne
  if (measureUnits && measureUnits.length > 0) {
    for (const unit of measureUnits) {
      if (unit.wzorce_regex) {
        // Buduj regex z wzorcow z bazy: (\d+)\s*(?:wzorce)
        const patterns = unit.wzorce_regex.split('|').map(p => p.trim()).filter(p => p);
        if (patterns.length > 0) {
          const regexPattern = new RegExp(`(\\d+(?:[\\.,]\\d+)?)\\s*(?:${patterns.join('|')})`, 'i');
          const match = text.match(regexPattern);
          if (match) {
            const quantity = match[1].replace(',', '.');
            return { quantity, unit: unit.nazwa };
          }
        }
      }
    }
  }

  // Fallback na hardkodowane wzorce jesli brak jednostek z DB
  const unitPatterns = [
    { pattern: /(\d+)\s*(?:kaps|kap|caps|cap|kapsułek|kapsułki|kapsulek|kapsulki)/i, unit: 'kapsułki' },
    { pattern: /(\d+)\s*(?:tabs|tab|tabl|tabletek|tabletki)/i, unit: 'tabletki' },
    { pattern: /(\d+)\s*(?:sasz|saszetki|saszetke|saszetek)/i, unit: 'sztuki' },
    { pattern: /(\d+)\s*(?:szt|sztuk|sztuki)/i, unit: 'sztuki' },
    { pattern: /(\d+(?:[.,]\d+)?)\s*ml/i, unit: 'ml' },
    { pattern: /(\d+(?:[.,]\d+)?)\s*g(?!r)(?!\s*mg)/i, unit: 'gramy' },
  ];

  for (const { pattern, unit } of unitPatterns) {
    const match = text.match(pattern);
    if (match) {
      const quantity = match[1].replace(',', '.');
      return { quantity, unit };
    }
  }

  return { quantity: '', unit: 'nieustawiono' };
};

export const extractWeight = (name, description) => {
  if (!name && !description) return '';

  const text = `${name || ''} ${description || ''}`;

  // Szukaj wzorcow wagi
  const weightPatterns = [
    /(\d+(?:\.\d+)?)\s*kg/i,
    /(\d+(?:\.\d+)?)\s*g(?!\s*mg)/i,
    /(\d+(?:\.\d+)?)\s*mg/i
  ];

  for (const pattern of weightPatterns) {
    const match = text.match(pattern);
    if (match) {
      return match[0];
    }
  }

  return '';
};

export const extractManufacturer = (name, description, manufacturers = []) => {
  if (!name && !description) return '';

  const text = `${name || ''} ${description || ''}`.toLowerCase();

  // Uzywaj producentow z bazy danych (z panelu administracyjnego)
  if (manufacturers && manufacturers.length > 0) {
    for (const mfr of manufacturers) {
      if (mfr.nazwa && text.includes(mfr.nazwa.toLowerCase())) {
        return mfr.nazwa;
      }
    }
  }

  return '';
};

export const getDisplayPrice = (product) => {
  if (product.hasSpecialPrice) {
    return {
      price: product.specialPriceBrutto,
      type: 'special',
      oldPrice: product.cena_sprzedazy_brutto
    };
  }
  return {
    price: product.cena_sprzedazy_brutto,
    type: 'normal',
    oldPrice: null
  };
};

export const calculateMargin = (product, useSpecialPrice = false) => {
  // Kaucja: wykluczona z marzy (nie liczymy, netto == brutto)
  if (product && (product.typ_produktu === 'kaucja' || parseFloat(product.stawka_vat) === -2)) {
    return null;
  }

  // Marza zawsze obliczana od cen NETTO
  const sellPriceNetto = parseFloat(useSpecialPrice && product.hasSpecialPrice ?
    product.specialPriceNetto : product.cena_sprzedazy_netto) || 0;
  // POPRAWKA: Priorytetowo uzyj purchase_price z API warehouse prices (z faktur)
  const buyPriceNetto = parseFloat(product.purchase_price || product.cena_zakupu_netto || product.cena_zakupu) || 0;

  if (sellPriceNetto <= 0 || buyPriceNetto <= 0) {
    return { percent: 0, amount: 0 };
  }

  const marginAmount = sellPriceNetto - buyPriceNetto;
  // MARZA = zysk / cena sprzedazy netto (NIE narzut = zysk / koszt)
  const marginPercent = Math.round((marginAmount / sellPriceNetto) * 100);

  return {
    percent: marginPercent,
    amount: parseFloat(marginAmount.toFixed(2))
  };
};

export const calculatePricePerUnit = (price, weight, unit) => {
  if (!price || !weight || weight <= 0 || unit === 'nieustawiono') return 0;

  const numPrice = parseFloat(price);
  const numWeight = parseFloat(weight);

  switch(unit) {
    case 'gramy':
    case 'ml':
      return (numPrice / numWeight) * 100;
    case 'tabletki':
    case 'kapsułki':
    case 'sztuki':
      return numPrice / numWeight;
    default:
      return 0;
  }
};

export const calculatePriceFromUnit = (unitPrice, targetWeight, unit) => {
  if (!unitPrice || !targetWeight || targetWeight <= 0 || unit === 'nieustawiono') return 0;

  const numUnitPrice = parseFloat(unitPrice);
  const numTargetWeight = parseFloat(targetWeight);

  switch(unit) {
    case 'gramy':
    case 'ml':
      return (numUnitPrice / 100) * numTargetWeight;
    case 'tabletki':
    case 'kapsułki':
    case 'sztuki':
      return numUnitPrice * numTargetWeight;
    default:
      return 0;
  }
};

export const calculatePriceFromNetto = (netto, vatRate) => {
  if (!netto || isNaN(netto)) return '';
  const brutto = parseFloat(netto) * (1 + vatRate / 100);
  return brutto.toFixed(2);
};

export const calculatePriceFromBrutto = (brutto, vatRate) => {
  if (!brutto || isNaN(brutto)) return '';
  const netto = parseFloat(brutto) / (1 + vatRate / 100);
  return netto.toFixed(2);
};

// Compute unit price info for label display
export const computeUnitPriceInfo = (product, displayPrice, measureUnits = []) => {
  const packageQty = product.packageQuantity || extractPackageQuantity(product, product.nazwa, product.opis, measureUnits) || '';
  let detectedUnit = '';
  let detectedQty = 0;

  if (packageQty) {
    const match = packageQty.match(/(\d+)\s*(\w+)/i);
    if (match) {
      detectedQty = parseFloat(match[1]) || 0;
      detectedUnit = match[2].toLowerCase();
    }
  }

  let packageInfo = '';
  let unitPriceText = '';

  if (detectedUnit && detectedQty > 0) {
    if (detectedUnit.match(/^(tab|tabl|tablet)/i)) {
      packageInfo = `${detectedQty} tabl`;
      unitPriceText = `${(displayPrice / detectedQty).toFixed(2)} zł/tabl`;
    } else if (detectedUnit.match(/^(kaps|kap|capsul)/i)) {
      packageInfo = `${detectedQty} kaps`;
      unitPriceText = `${(displayPrice / detectedQty).toFixed(2)} zł/kaps`;
    } else if (detectedUnit.match(/^(sasz|saszetk)/i)) {
      packageInfo = `${detectedQty} sasz`;
      unitPriceText = `${(displayPrice / detectedQty).toFixed(2)} zł/sasz`;
    } else if (detectedUnit === 'ml') {
      packageInfo = `${detectedQty} ml`;
      unitPriceText = `${((displayPrice / detectedQty) * 100).toFixed(2)} zł/100ml`;
    } else if (detectedUnit === 'g') {
      packageInfo = `${detectedQty} g`;
      unitPriceText = `${((displayPrice / detectedQty) * 100).toFixed(2)} zł/100g`;
    } else if (detectedUnit.match(/^(szt|pcs)/i)) {
      packageInfo = `${detectedQty} szt`;
      unitPriceText = `${(displayPrice / detectedQty).toFixed(2)} zł/szt`;
    } else {
      packageInfo = `${detectedQty} ${detectedUnit}`;
      unitPriceText = `${(displayPrice / detectedQty).toFixed(2)} zł/${detectedUnit}`;
    }
  } else {
    // Fallback na jednostka_wagi z bazy danych
    const unit = product.cenowka?.jednostka_wagi || product.jednostka_wagi || product.unit || '';
    const weightVal = parseFloat(product.cenowka?.waga || product.waga || product.weight || 0);
    const quantityVal = detectedQty || parseFloat(product.ilosc || 0);

    if (unit && (weightVal > 0 || quantityVal > 0)) {
      const unitLower = unit.toLowerCase();

      if (unitLower === 'gramy' || unitLower === 'g') {
        packageInfo = `${weightVal || quantityVal} g`;
        unitPriceText = `${((displayPrice / (weightVal || quantityVal)) * 100).toFixed(2)} zł/100g`;
      } else if (unitLower === 'ml') {
        packageInfo = `${weightVal || quantityVal} ml`;
        unitPriceText = `${((displayPrice / (weightVal || quantityVal)) * 100).toFixed(2)} zł/100ml`;
      } else if (unitLower === 'kapsułki' || unitLower === 'kaps' || unitLower === 'kapsułka') {
        packageInfo = `${quantityVal || weightVal} kaps`;
        unitPriceText = `${(displayPrice / (quantityVal || weightVal)).toFixed(2)} zł/kaps`;
      } else if (unitLower === 'tabletki' || unitLower === 'tabl' || unitLower === 'tabletka') {
        packageInfo = `${quantityVal || weightVal} tabl`;
        unitPriceText = `${(displayPrice / (quantityVal || weightVal)).toFixed(2)} zł/tabl`;
      } else if (unitLower === 'sztuki' || unitLower === 'szt' || unitLower === 'sztuka') {
        packageInfo = `${quantityVal || weightVal} szt`;
        unitPriceText = `${(displayPrice / (quantityVal || weightVal)).toFixed(2)} zł/szt`;
      } else if (quantityVal > 0 && quantityVal !== 1) {
        packageInfo = `${quantityVal} ${unit}`;
        unitPriceText = `${(displayPrice / quantityVal).toFixed(2)} zł/${unit}`;
      }
    }
  }

  return { packageInfo, unitPriceText };
};
